/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState } from 'react';
import SelectBox from '@components/form/SelectBox';

import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';
import PropTypes from 'prop-types';

const propTypes = {
  testID: PropTypes.string,
  editable: PropTypes.bool,
  isRequired: PropTypes.bool,
  multiple: PropTypes.bool,
  hideLabel: PropTypes.bool,
  allTeams: PropTypes.bool,
  hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  customFilter: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
  isAll: PropTypes.bool,
};

const defaultProps = {
  editable: true,
  isRequired: false,
  multiple: false,
  hideLabel: false,
  hasError: false,
  onChange: () => {},
  customFilter: {},
  value: '',
  allTeams: false,
  isAll: false,
};

const limit = 10;

const SalesTeamSearch = (props) => {
  const {
    editable = true,
    isRequired = false,
    multiple = false,
    hideLabel = false,
    hasError = false,
    customFilter = {},
    value = '',
    allTeams = false,
    isAll = false,
  } = props;

  const [loading, setLoading] = useState({
    initial: false,
    loadMore: false,
  });
  const [options, setOptions] = useState([]);
  const [skip, setSkip] = useState(0);
  const [salesTeam, setSalesTeam] = useState([]);

  const getSalesTeam = useApi(MsB2b.getSalesTeam);

  const onSearch = async (val, newSkip = 0) => {
    if (newSkip) {
      setLoading({ ...loading, loadMore: true });
    } else {
      setLoading({ ...loading, initial: true });
    }
    try {
      let filter = {};
      const arrayNewFilter = [];
      for (let i = 0; i < salesTeam.length; i++) {
        arrayNewFilter.push(salesTeam[i]._id);
      }
      const filterSales = {
        $or: [
          { branch_id: { $exists: false } },
          { branch_id: null },
          { _id: { $in: arrayNewFilter } },
          { is_show_all: true },
        ],
      };
      if (customFilter && Object.keys(customFilter).length) {
        if (salesTeam.length > 0) {
          filter = filterSales;
        } else {
          filter = customFilter;
        }
      }
      if (val) {
        filter.name = { $regex: val, $options: 'i' };
      }

      if (!val && filter.name) {
        delete filter.name;
      }

      const response = await getSalesTeam.request({
        params: {
          filter,
          skip: newSkip,
          limit: limit,
        },
      });

      if (response.status == 200 && response?.data?.data?.length) {
        const result = response.data.data.map((item) => {
          if (item._id) {
            item.id = item._id;
          }
          return item;
        });
        if (allTeams && newSkip == 0) {
          result.unshift({ id: 'all', name: 'All' });
        }
        if (newSkip) {
          setOptions(options.concat(result));
        } else {
          if (isAll) {
            result.unshift({ id: 'all', name: 'All' });
          }
          setOptions(result);
        }
      } else {
        if (!newSkip) {
          setOptions([]);
        }
      }

      if (newSkip) {
        setLoading({ ...loading, loadMore: false });
      } else {
        setLoading({ ...loading, initial: false });
      }
    } catch (error) {
      if (newSkip) {
        setLoading({ ...loading, loadMore: false });
      } else {
        setLoading({ ...loading, initial: false });
      }
    }
  };

  return (
    <SelectBox
      {...props}
      label="Sales Team"
      options={options}
      value={value}
      placeholder={
        loading.initial || loading.loadMore ? 'Loading...' : 'Select Sales Team'
      }
      trackBy="id"
      labelKey="name"
      isRequired={isRequired}
      multiple={multiple}
      hideLabel={hideLabel}
      editable={editable && !loading.initial}
      hasError={hasError}
      loading={loading.initial}
      loadingCallback={loading.loadMore}
      withCallback={true}
      withLoadMore={true}
      loadMoreEmpty={skip >= options.length}
      searchCallback={(val) => {
        setSkip(0);
        onSearch(val);
      }}
      onLoadMore={(val) => {
        const newSkip = skip + 10;
        setSkip(newSkip);
        onSearch(val, newSkip);
      }}
      onChange={(val, val2) => {
        const newVal2 = val2 || [];
        setSalesTeam(newVal2);
        let finalValue = val;
        if (multiple) {
          if (val?.length) {
            const allIndex = val.findIndex((a) => a?.id == 'all');
            if (allIndex >= 0 && val.length >= 1) {
              if (allIndex == 0 && val.length != 1) {
                val.splice(allIndex, 1);
                finalValue = val;
              } else {
                finalValue = [{ id: 'all', name: 'All' }];
              }
            }
          }
        }
        props.onChange(finalValue, val2);
      }}
      onReady={() => {
        setSkip(0);
        onSearch();
      }}
    />
  );
};

SalesTeamSearch.propTypes = propTypes;
SalesTeamSearch.defaultProps = defaultProps;

export default SalesTeamSearch;
